// src/sections/privacy/SeedPhraseConfirmStep.tsx
// Randomly selects 5 positions and asks user to type the correct words.
// Prevents users from proceeding without actually saving seed phrase.

import { useState, useMemo } from "react";
import { Button, Text } from "@opal/components";

interface Props {
  allWords: string[];   // 24 words from seed phrase
  onConfirmed: () => void;
  onBack: () => void;
  error: string | null;
  isLoading?: boolean;
}

const N_WORDS = 5; // number of words to verify

export function SeedPhraseConfirmStep({
  allWords,
  onConfirmed,
  onBack,
  error,
  isLoading = false,
}: Props) {
  // Pick N random, sorted positions — stable across re-renders
  const positions = useMemo<number[]>(() => {
    const chosen = new Set<number>();
    while (chosen.size < N_WORDS) {
      chosen.add(Math.floor(Math.random() * 24));
    }
    return [...chosen].sort((a, b) => a - b);
  }, []); // empty deps — intentionally stable

  const [answers, setAnswers] = useState<Record<number, string>>({});
  const [wrongPositions, setWrongPositions] = useState<number[]>([]);

  function setAnswer(pos: number, value: string) {
    setAnswers(prev => ({ ...prev, [pos]: value }));
    // Clear wrong state for this position when user types
    setWrongPositions(prev => prev.filter(p => p !== pos));
  }

  function handleSubmit() {
    const wrong = positions.filter(
      pos => answers[pos]?.trim().toLowerCase() !== allWords[pos]?.toLowerCase()
    );
    if (wrong.length > 0) {
      setWrongPositions(wrong);
      return;
    }
    setWrongPositions([]);
    onConfirmed();
  }

  return (
    <div className="space-y-4">
      <Text color="subtle">
        Nhập <strong>{N_WORDS} từ</strong> tại các vị trí sau để xác nhận bạn đã lưu cụm từ khôi phục:
      </Text>

      <div className="space-y-3">
        {positions.map(pos => {
          const isWrong = wrongPositions.includes(pos);
          return (
            <div key={pos} className="flex items-center gap-3">
              <span
                className="text-xs font-mono text-[var(--text-03)] w-8 text-right shrink-0 select-none"
                aria-hidden="true"
              >
                #{pos + 1}
              </span>
              <input
                id={`seed-confirm-word-${pos}`}
                type="text"
                value={answers[pos] ?? ""}
                onChange={e => setAnswer(pos, e.target.value)}
                onKeyDown={e => { if (e.key === "Enter") handleSubmit(); }}
                placeholder={`Từ thứ ${pos + 1}`}
                aria-label={`Từ thứ ${pos + 1} trong cụm từ khôi phục`}
                aria-invalid={isWrong}
                autoComplete="off"
                autoCorrect="off"
                autoCapitalize="off"
                spellCheck={false}
                disabled={isLoading}
                className={[
                  "flex-1 rounded-md border px-3 py-1.5 text-sm font-mono bg-[var(--surface-01)]",
                  "focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]",
                  isWrong
                    ? "border-[var(--status-error-02)] bg-[var(--status-error-00)]"
                    : "border-[var(--border-01)]",
                ].join(" ")}
              />
            </div>
          );
        })}
      </div>

      {wrongPositions.length > 0 && (
        <div className="rounded-md border border-[var(--status-error-02)] bg-[var(--status-error-00)] p-3 text-sm text-[var(--status-error-02)]" role="alert" aria-live="assertive">
          Từ tại vị trí {wrongPositions.map(i => `#${i + 1}`).join(", ")} không đúng.
          Hãy kiểm tra lại cụm từ và nhập chính xác.
        </div>
      )}

      {error && (
        <div className="rounded-md border border-[var(--status-error-02)] bg-[var(--status-error-00)] p-3 text-sm text-[var(--status-error-02)]" role="alert" aria-live="assertive">
          {error}
        </div>
      )}

      <div className="flex gap-2 pt-2">
        <Button
          id="seed-confirm-back-btn"
          variant="secondary"
          onClick={onBack}
          disabled={isLoading}
        >
          ← Quay lại
        </Button>
        <Button
          id="seed-confirm-submit-btn"
          variant="primary"
          onClick={handleSubmit}
         
          disabled={positions.some(p => !answers[p]?.trim())}
          className="flex-1"
        >
          Xác nhận cụm từ
        </Button>
      </div>
    </div>
  );
}
